import React from "react";
import { Field, UseFieldConfig } from "react-final-form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "components/ui/select";
import FieldGroup, { FieldGroupProps } from "ui/Form/Element/FieldGroup";
import FieldControl, { FieldControlProps } from "ui/Form/Element/FieldControl";
import Label, { LabelProps } from "ui/Form/Element/Label";
import Feedback from "ui/Form/Element/Feedback";
import { Options } from "ui/Form/Element/Select";
import { cn } from "lib/utils";
import {
    selectContentClassName,
    selectItemClassName,
    selectTriggerClassName,
} from "./selectStyles";

export function normalizeSelectValue(raw: unknown): string | undefined {
    if (raw === undefined || raw === null) {
        return undefined;
    }
    if (typeof raw === "object" && "toString" in raw && typeof raw.toString === "function") {
        const key = raw.toString();
        if (key && key !== "[object Object]") {
            return key;
        }
    }
    const str = String(raw).trim();
    if (str === "" || str === "[object Object]") {
        return undefined;
    }
    return str;
}

export interface RadixSelectProps {
    labelProps?: LabelProps & { label: React.ReactNode };
    fieldGroupProps?: FieldGroupProps;
    fieldControlProps?: FieldControlProps;
    inputProps: {
        name: string;
        id?: string;
        placeholder?: string;
        options: Options[];
        required?: boolean;
        disabled?: boolean;
        /** Applied to the trigger; alias for trigger styling alongside TextField `className`. */
        className?: string;
        triggerClassName?: string;
        contentClassName?: string;
        itemClassName?: string;
    };
}

type Props = RadixSelectProps & UseFieldConfig<string>;

const RadixSelect: React.FC<Props> = ({
    labelProps,
    fieldGroupProps,
    fieldControlProps,
    inputProps,
    ...fieldProps
}) => {
    const {
        name,
        id,
        placeholder,
        options,
        required,
        disabled,
        className,
        triggerClassName,
        contentClassName,
        itemClassName,
    } = inputProps;
    const fieldId = id ?? name;

    return (
        <Field name={name} {...fieldProps}>
            {({ input, meta }) => {
                const error = Boolean(meta.error || meta.submitError);
                const selectValue = normalizeSelectValue(input.value);

                return (
                    <FieldGroup error={error} {...fieldGroupProps}>
                        {labelProps !== undefined && (
                            <Label
                                htmlFor={fieldId}
                                disabled={disabled}
                                error={error}
                                required={required}
                                {...labelProps}
                            >
                                {labelProps.label}
                            </Label>
                        )}
                        <FieldControl disabled={disabled} error={error} {...fieldControlProps}>
                            <Select
                                key={`${name}-${selectValue ?? ""}`}
                                value={selectValue}
                                onValueChange={input.onChange}
                                onOpenChange={(open) => {
                                    if (!open) {
                                        input.onBlur();
                                    }
                                }}
                                required={required}
                                disabled={disabled}
                            >
                                <SelectTrigger
                                    id={fieldId}
                                    aria-invalid={error}
                                    className={cn(
                                        selectTriggerClassName,
                                        className,
                                        triggerClassName,
                                    )}
                                >
                                    <SelectValue placeholder={placeholder} />
                                </SelectTrigger>
                                <SelectContent
                                    sideOffset={8}
                                    className={cn(selectContentClassName, contentClassName)}
                                >
                                    {options.map((option) => (
                                        <SelectItem
                                            key={option.key ?? String(option.value)}
                                            value={String(option.value)}
                                            disabled={option.disabled}
                                            className={cn(selectItemClassName, itemClassName)}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FieldControl>
                        {error && (
                            <Feedback className="field__feedback error">
                                {meta.error || meta.submitError}
                            </Feedback>
                        )}
                    </FieldGroup>
                );
            }}
        </Field>
    );
};

export default RadixSelect;
